import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../config/firebase/config';
import { Restaurant } from '../../../types/restaurant';
import { ArrowLeft, Download, ChevronLeft, ChevronRight } from 'lucide-react';

interface Transaction {
  id: string;
  amount: number;
  tip: number;
  tax: number;
  subtotal: number;
  total: number;
  status: 'completed' | 'failed' | 'pending';
  processor: string;
  tableNumber?: string;
  customerEmail?: string;
  createdAt: Date;
}

const ITEMS_PER_PAGE = 15;

export default function RestaurantTransactionsScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    try {
      // Load restaurant
      const restaurantDoc = await getDoc(doc(db, 'restaurants', id));
      if (restaurantDoc.exists()) {
        setRestaurant({ id: restaurantDoc.id, ...restaurantDoc.data() } as Restaurant);
      }
      // Load transactions from restaurant-scoped path
      const transactionsRef = collection(db, 'restaurants', id, 'transactions');
      const transactionsQuery = query(
        transactionsRef,
        orderBy('createdAt', 'desc')
      );
      const transactionsSnap = await getDocs(transactionsQuery);
      
      const txData = transactionsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as Transaction[];
      setTransactions(txData);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: restaurant?.settings?.currency || 'DOP'
    }).format(amount);
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(tx => {
    // Status filter only
    if (statusFilter !== 'all' && tx.status !== statusFilter) return false;
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const exportToCSV = () => {
    const headers = ['Fecha', 'Monto', 'Estado', 'Procesador', 'Propina', 'Impuesto', 'Total'];
    const rows = filteredTransactions.map(tx => [
      tx.createdAt.toLocaleString('es-ES'),
      tx.amount,
      tx.status,
      tx.processor,
      tx.tip || 0,
      tx.tax || 0,
      tx.total
    ]);
    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${restaurant?.name}-transacciones-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando transacciones...</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="p-8">
        <div className="text-center">
          <p className="text-red-600">Restaurante no encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigate(`/super-admin/restaurants/${id}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Volver a los detalles del restaurante
        </button>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <Download size={18} />
          Exportar CSV
        </button>
      </div>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{restaurant.name}</h1>
        <p className="text-gray-600 mt-1">Historial de transacciones</p>
      </div>

      {/* Filter Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">Todos los estados</option>
            <option value="completed">Completada</option>
            <option value="failed">Fallida</option>
            <option value="pending">Pendiente</option>
          </select>
          <div className="text-sm text-gray-600">
            {filteredTransactions.length} transacción{filteredTransactions.length !== 1 ? 'es' : ''} encontrada{filteredTransactions.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">FECHA Y HORA</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MONTO</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PROPINA</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">TOTAL</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ESTADO</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PROCESADOR</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MESA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedTransactions.map((tx) => (
                <tr key={tx.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {tx.createdAt.toLocaleDateString('es-ES')}<br />
                    <span className="text-xs text-gray-500">{tx.createdAt.toLocaleTimeString('es-ES')}</span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {formatCurrency(tx.amount)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatCurrency(tx.tip || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-900">
                    {formatCurrency(tx.total)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      tx.status === 'completed' ? 'bg-green-100 text-green-700' :
                      tx.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {tx.status === 'completed' ? 'Exitosa' : tx.status === 'failed' ? 'Fallida' : 'Pendiente'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${
                      tx.processor === 'stripe' ? 'bg-purple-100 text-purple-700' :
                      tx.processor === 'cardnet' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {tx.processor}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {tx.tableNumber || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {paginatedTransactions.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No se encontraron transacciones</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Mostrando {startIndex + 1}-{Math.min(endIndex, filteredTransactions.length)} de {filteredTransactions.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 rounded ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}